/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { HomeDrawer } from './Home';
import { IncidentPageDrawer } from './IncidentPage';
import { MapControls } from '../map/MapControls';
import { MapDrawer } from '../components/MapDrawer';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useMatch,
  useSearchParams,
  useParams,
} from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { incidentConfigByIdAtom } from '../config/api';
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { fetchWeatherStats, type ChartPoint } from '../services/weatherHistory';
import { useWeatherEventsLayer } from '../map/layers/WeatherEventsLayer';
import { MapChartControls } from '../map/MapChartControls';
import type { MapView } from '../services/types';
import { ErrorBoundary } from '../components/ErrorBoundary';

const Map = React.lazy(() => import('../map/Map'));

// 'post' is not a standalone map view, so it is not a member.
export type PrimaryLayer = MapView;

export const MapPage = React.memo(() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const canopyKmlId = searchParams.get('canopy_kml_id');

  const [selectedMap, setSelectedMap] = React.useState<PrimaryLayer>(
    canopyKmlId != null ? 'canopy' : 'wow'
  );

  // The deep link names the swath the Canopy product frames. Once another
  // product replaces Canopy it describes nothing on the map, and left in the
  // URL it kept naming that swath as the exposure event the structures views
  // key on (StructuresLayer.tsx useExposureEventId). Removed on the product
  // transition rather than in the click handlers: the product is switched
  // from the controls panel and from HomeDrawer's product avatars and event
  // lists, and HomeDrawer's memo keeps its handlers' closures, search params
  // included, stale.
  const previousMapRef = React.useRef(selectedMap);
  React.useEffect(() => {
    const leftCanopy = previousMapRef.current === 'canopy' && selectedMap !== 'canopy';
    previousMapRef.current = selectedMap;
    if (leftCanopy && searchParams.has('canopy_kml_id')) {
      searchParams.delete('canopy_kml_id');
      setSearchParams(searchParams, { replace: true });
    }
  }, [selectedMap, searchParams, setSearchParams]);

  // Aggregated time-series chart points for the selected map (one dot per
  // date/category bucket), plus a loading flag so the chart can show a spinner
  // instead of an empty-state message while the fetch is in flight.
  const [weatherEvents, setWeatherEvents] = useState<ChartPoint[] | null>(null);
  const [weatherEventsLoading, setWeatherEventsLoading] = useState<boolean>(false);

  const weatherHistoryCache = useRef<Record<string, ChartPoint[]>>({});

  // A deep link arriving after mount switches the product; the initial one is
  // already in the state above, so this only tracks later changes.
  const [lastCanopyKmlId, setLastCanopyKmlId] = React.useState(canopyKmlId);
  if (lastCanopyKmlId !== canopyKmlId) {
    setLastCanopyKmlId(canopyKmlId);
    if (canopyKmlId) {
      setSelectedMap('canopy');
    }
  }

  const title = useMapTitle();
  const match = useMatch({
    path: '/:incidentId/:sectionId/:viewType/:attributeId?',
  });
  const isHomePage = useMatch('/') !== null;

  // Fetch the time-series chart points for the selected map. Only the home
  // dashboard renders the chart (MapChartControls shows incident stats
  // instead), so an incident view must not pay for the aggregation.
  useEffect(() => {
    if (!isHomePage) {
      return;
    }

    const cached = weatherHistoryCache.current[selectedMap];
    if (cached) {
      setWeatherEvents(cached);
      setWeatherEventsLoading(false);
      return;
    }

    let cancelled = false;
    setWeatherEventsLoading(true);
    setWeatherEvents(null);

    fetchWeatherStats(selectedMap)
      .then(result => {
        if (cancelled) return;
        weatherHistoryCache.current[selectedMap] = result;
        setWeatherEvents(result);
      })
      .catch(error => {
        console.error('Error fetching weather stats:', error);
        if (!cancelled) setWeatherEvents([]);
      })
      .finally(() => {
        if (!cancelled) setWeatherEventsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isHomePage, selectedMap]);

  // Create weather events layer
  const weatherEventsLayer = useWeatherEventsLayer({
    visible: match == null, // Only visible when NOT on an incident route
    mapType: selectedMap,
  });

  const params = useParams();
  const location = useLocation();

  const mapContainerRef = React.useRef<HTMLDivElement>(null!);

  return (
    <React.Suspense fallback={null}>
      <Box sx={{ width: '100%' }}>
        <Map
          title={title}
          selectedMap={selectedMap}
          layers={[weatherEventsLayer]}
          outerChildren={
            <Box
              sx={{
                width: '100%',
                height: 'calc(100vh - 134px) !important',
                pointerEvents: 'none',
              }}
            >
              <Box
                ref={mapContainerRef}
                sx={{
                  width: '100%',
                  height: 'calc(100vh - 134px) !important',
                  pointerEvents: 'none',
                  position: 'relative',
                }}
              >
                <>
                  {/* The layers panel is wrapped from outside rather than
                      inside its own window, unlike the stats panel: the
                      structures-availability and exposure-event reads it needs
                      run in its own body, above any chrome it renders. */}
                  <ErrorBoundary region="The layers panel" resetKey={location.pathname}>
                    <MapControls selectedMap={selectedMap} setSelectedMap={setSelectedMap} />
                  </ErrorBoundary>
                  <MapChartControls
                    isHomePage={isHomePage}
                    scatterPlotEvents={weatherEvents}
                    scatterPlotLoading={weatherEventsLoading}
                    selectedMap={selectedMap}
                    params={params}
                  />
                </>
              </Box>
            </Box>
          }
        >
          <Routes>
            <Route path=":incidentId/:sectionId" element={<Navigate replace to="map" />} />
          </Routes>
        </Map>
      </Box>
      <MapDrawer>
        {/* Inside the drawer, so a sidebar failure keeps the drawer, its
            toggle and the map beside it. The incident lists here are the only
            way out of a broken view, so the boundary sits above the routed
            sidebar content rather than around the page. */}
        <ErrorBoundary region="The sidebar" resetKey={location.pathname}>
          <Routes>
            <Route
              path=":incidentId/:sectionId/:viewType/:attributeId?"
              element={<IncidentPageDrawer />}
            />

            <Route
              path="/"
              element={<HomeDrawer selectedMap={selectedMap} setSelectedMap={setSelectedMap} />}
            />
          </Routes>
        </ErrorBoundary>
      </MapDrawer>
      <Routes>
        <Route path=":incidentId/:sectionId/:viewType/:attributeId?" element={null} />
      </Routes>
    </React.Suspense>
  );
});

const useMapTitle = () => {
  const isIncidentMap = useMatch({
    path: ':incidentId/:sectionId/:viewType/:attributeId?',
  });

  const incidentId = isIncidentMap?.params.incidentId;
  const incidentConfig = useAtomValue(incidentConfigByIdAtom(incidentId));

  return incidentConfig?.name ?? (isIncidentMap ? (incidentId ?? 'Overview') : 'Overview');
};
